import { useEffect, useRef } from 'react';
import { Modal, ModalBody, Button, Text, Stack } from '@7shifts/sous-chef-base';
import type { ErrorType } from '@src/api/punchTypes';
import { getErrorConfig } from './errorConfig';
import { useTranslation } from '@src/i18n';
import './ErrorModal.css';

const DEFAULT_AUTO_DISMISS_MS = 5000;

interface ErrorModalProps {
    errorType: ErrorType;
    message: string | null;
    onDismiss: () => void;
    autoDismissMs?: number;
}

export function ErrorModal({
    errorType,
    message,
    onDismiss,
    autoDismissMs = DEFAULT_AUTO_DISMISS_MS
}: ErrorModalProps) {
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;
    const { t } = useTranslation();

    useEffect(() => {
        const timer = setTimeout(() => onDismissRef.current(), autoDismissMs);
        return () => clearTimeout(timer);
    }, [autoDismissMs]);

    const { headerKey, fallbackMessageKey, Icon } = getErrorConfig(errorType);
    const displayMessage = message ?? t(fallbackMessageKey);

    return (
        <Modal onClose={onDismiss} testId="error-modal">
            <ModalBody>
                <Stack space={20} justifyContent="space-between" flexGrow={1}>
                    <Stack space={8} alignItems="center">
                        <div className="error-modal__icon-circle">
                            <Icon size="large" variant="solid" />
                        </div>
                        <Stack space={4} alignItems="center">
                            <Text as="h1" alignment="center">
                                {t(headerKey)}
                            </Text>
                            <Text
                                as="h4"
                                alignment="center"
                                color="var(--color-surface-on-color-subtle)"
                            >
                                {displayMessage}
                            </Text>
                        </Stack>
                    </Stack>
                    <Button
                        type="button"
                        theme="hollow"
                        size="full-width"
                        onClick={onDismiss}
                        testId="error-modal-dismiss"
                    >
                        {t('common.ok')}
                    </Button>
                </Stack>
            </ModalBody>
        </Modal>
    );
}
